<template>
  <div class="course-card">
    <div class="course-card__wrapper ui-shadow-10pt">
      <PromoTimer
        v-if="promo?.landing_card_banner"
        :images="promo.landing_card_banner.image"
        :end-promotion-date="promo.end"
        :bg-color="promo.landing_card_banner.background_color"
        :text="promo.landing_card_banner.text"
        :text-color="promo.landing_card_banner.text_color"
        class="course-card__promotion"
      />

      <div class="course-card__contents">
        <div class="course-card__picture-wrapper">
          <UiPicture
            :data="card.images"
            width="288"
            height="160"
            :is-lazy="false"
            class="course-card__picture"
          />

          <template v-for="(plaque, i) in card.plaques">
            <UiTooltip
              v-if="plaque.tag && plaque.tagTooltipContent"
              :key="`${i}-tooltip`"
              :title="plaque.tagTooltipContent.title"
              :text="plaque.tagTooltipContent.text"
              :offset="[0, 14]"
              :delay="[0, 150]"
              :duration="[150, 110]"
              placement="bottom-start"
              class="course-card__picture-tooltip-tag"
            >
              <UiTag
                :tag="plaque.tag"
                :bg-color="plaque.tagBgColor"
                :text-color="plaque.tagTextColor"
                :font-weight="plaque.tagFontWeight"
                size="medium"
                @click="handleTagClick"
              />
            </UiTooltip>

            <UiTag
              v-else-if="plaque.tag"
              :key="i"
              :tag="plaque.tag"
              :bg-color="plaque.tagBgColor"
              :text-color="plaque.tagTextColor"
              :font-weight="plaque.tagFontWeight"
              size="medium"
              class="course-card__picture-tag"
            />
          </template>
        </div>

        <div
          v-if="isSubscriptionAvailable"
          class="course-card__subscribe-button"
        >
          <SubscribeButtonSmart />
        </div>

        <InfosList
          :infos="card.infos"
          class="course-card__infos-list"
        />

        <div class="course-card__price">
          <CoursePrice
            :price="card.price"
            :program="card.program"
            tag-size="small"
            :button-size="xxl ? 'big' :'medium'"
            :access-type="accessType"
            :is-subscription-available="isSubscriptionAvailable"
            @click:pay="$emit('click:pay', $event)"
          />
        </div>
      </div>
    </div>

    <TermsLink
      v-if="accessType === AccessTypeEnum.Free"
      link="https://skillbox.ru/legal-docs/chou/file/frimium_academika.pdf"
    >
      Условия акции
    </TermsLink>

    <TermsLink
      v-else-if="isSubscriptionAvailable"
      link="https://skillbox.ru/legal-docs/chou/file/subscribe-pro.pdf"
    >
      Условия подписки
    </TermsLink>
  </div>
</template>

<script setup lang="ts">
  import UiPicture from '@academica-box/components/Media/UiPicture/UiPicture.vue'
  import UiTag from '@academica-box/components/Plaques/UiTag/UiTag.vue'
  import UiTooltip from '@academica-box/components/InteractiveElements/UiTooltip/UiTooltip.vue'
  import type { Promo } from '@academica-box/swagger/marketplace'
  import type { CourseCardBlock } from '@academica-box/helpers/mappingCourseData'
  import TermsLink from '@/components/TermsLink/TermsLink.vue'
  import CoursePrice from '@/components/Price/CoursePrice/CoursePrice.vue'
  import PromoTimer from '@/components/PromoTimer/PromoTimer.vue'
  import SubscribeButtonSmart from '@/components/PromoButton/SubscribeButton.smart/SubscribeButton.smart.vue'
  import InfosList from '@/components/InfosList/InfosList.vue'

  import { AccessTypeEnum } from '@/helpers/mappingCourseData'

  const config = useRuntimeConfig()
  const viewport = useViewport()

  const props = defineProps<{
    card: CourseCardBlock['card'];
    promo: Promo | null;
    accessType: AccessTypeEnum;
    duration?: number;
    isSubscriptionAvailable: boolean;
    isUserSubscriptionActive: boolean;
  }>()

  defineEmits(['click:pay'])

  const xxl = computed(() => viewport.isGreaterThan('xxl'))

  const handleTagClick = () => {
    if (!props.isSubscriptionAvailable) {
      return
    }

    if (props.isUserSubscriptionActive) {
      location.href = `${config.public.frontUrl}/catalog/?is_subscribe_available=1`
    } else {
      location.href = `${config.public.frontUrl}/pro/`
    }
  }
</script>

<style lang="scss">
  @use '@academica-box/utils/styles/mixins/UiMedia' as *;
  @use '@academica-box/utils/styles/mixins/UiReset' as *;
  @use '@academica-box/styles/UiTypography/mixins/f' as *;

  .course-card {
    @include media(md-max) {
      display: none;
    }
  }

  .course-card__wrapper {
    @include media(lg) {
      width: 288px;
      margin-bottom: 12px;
      border-radius: 14px;
      overflow: hidden;
      background-color: var(--bg-with-shadow-color);
    }

    @include media(xl) {
      width: 320px;
    }

    @include media(1600) {
      width: 408px;
    }
  }

  .course-card__picture-wrapper {
    display: grid;
  }

  .course-card__picture {
    grid-row: 1/1;
    grid-column: 1/1;

    @include media(lg) {
      height: 160px;
    }

    @include media(xl) {
      height: 180px;
    }

    @include media(1600) {
      height: 220px;
    }
  }

  .course-card__picture-tag,
  .course-card__picture-tooltip-tag {
    z-index: 1;
    grid-row: 1/1;
    grid-column: 1/1;
    align-self: flex-start;
    justify-self: flex-start;
    margin: 16px;
  }

  .course-card__picture-tooltip-tag {
    cursor: pointer;
  }

  .course-card__subscribe-button {
    padding: 16px 16px 4px;
  }

  .course-card__price {
    @include media(lg) {
      padding: 8px 16px 16px;
    }

    @include media(1600) {
      padding: 16px;
    }
  }
</style>
